import React, { createContext, useContext, useEffect, useRef, useState } from 'react';
import { useParams } from 'react-router-dom';
import {
	syncSinglePageLevelScriptConfig,
	syncBulkPageLevelScriptConfig
} from '../../api/pageLevelScriptConfig';
import { toast } from 'react-toastify';
import { PAGE_LEVEL_SCRIPT_CONFIG_MODES } from '../../core/constants';
import { UserContext } from '../UserContext';
import { UnitsContext } from '../UnitsContext';
import {
	cleanPageLevelScriptConfigData,
	getNewPageObjectForPageLevelScriptConfig,
	getProcessedPageLevelConfigArrDataFromMapping
} from './utils';
import {
	createPageLevelScriptConfig,
	updatePageLevelScriptConfig
} from '../../api/pageLevelScriptConfig';
import { getPageLevelScriptConfig } from '../../api/pageLevelScriptConfig';

const pageLevelScriptConfigContext = createContext();

const PageLevelScriptConfigProvider = ({ children }) => {
	const previousPageLevelScriptCouchBaseConfig = useRef({});
	const [pageLevelScriptConfig, setPageLevelScriptConfigData] = useState([]);
	const [pageLevelScriptConfigMode, setPageLevelScriptConfigMode] = useState(
		PAGE_LEVEL_SCRIPT_CONFIG_MODES.PAGE_CONFIG_LOADING
	);
	const [pageLevelScriptConfigPage, setPageLevelScriptConfigPage] = React.useState(1);

	const { siteId } = useParams();
	const { user } = useContext(UserContext);
	const { siteRawConfig } = useContext(UnitsContext);

	useEffect(() => {
		if (siteId) {
			setPageLevelScriptConfigAndMode();
		} else {
			setPageLevelScriptConfigMode(PAGE_LEVEL_SCRIPT_CONFIG_MODES.PAGE_CONFIG_ERROR);
		}
	}, [siteId]);

	/**
	 * Sets the page level script config and updates the mode based on the response.
	 * This function fetches the page level script config from the backend
	 * and sets it in the state.
	 * @returns {Promise<void>} A promise that resolves when the operation is complete.
	 */
	const setPageLevelScriptConfigAndMode = async () => {
		try {
			const response = await getPageLevelScriptConfig(siteId);
			if (response.status === 200) {
				const pageLevelScriptConfig = response.data.data.pagelevelConfig;
				const configArr = getProcessedPageLevelConfigArrDataFromMapping(pageLevelScriptConfig);
				setPageLevelScriptConfigMode(PAGE_LEVEL_SCRIPT_CONFIG_MODES.SITE_LEVEL_UPDATE_PAGE_CONFIGS);

				setPageLevelScriptConfigData(configArr);
				previousPageLevelScriptCouchBaseConfig.current = configArr;
			} else {
				setPageLevelScriptConfigMode(PAGE_LEVEL_SCRIPT_CONFIG_MODES.PAGE_CONFIG_ERROR);
			}
		} catch (err) {
			if (err.response.status === 404) {
				setPageLevelScriptConfigMode(PAGE_LEVEL_SCRIPT_CONFIG_MODES.PAGE_CONFIG_NOT_FOUND);
			} else {
				setPageLevelScriptConfigMode(PAGE_LEVEL_SCRIPT_CONFIG_MODES.PAGE_CONFIG_ERROR);
			}
		}
	};

	/**
	 * Adds a new page to the page level script config.
	 * This function is used to add a new,
	 * empty config object to the current list.
	 */
	const handlePageLevelScriptConfigAddPage = () => {
		setPageLevelScriptConfigData((prevData) => {
			const clonedPrevData = structuredClone(prevData);
			const newPage = getNewPageObjectForPageLevelScriptConfig();
			clonedPrevData.push(newPage);
			return clonedPrevData;
		});
	};

	/**
	 * Deletes a page from the page level script config.
	 * @param {string} id - The unique id of the page config to be deleted.
	 */
	const deletePageLevelScriptConfigPage = (id) => {
		setPageLevelScriptConfigPage((page) => {
			if (page !== 1) {
				return page - 1;
			}
			return page;
		});

		setPageLevelScriptConfigData((prevData) => {
			const clonedPrevData = structuredClone(prevData);
			const updatedConfigWithDeletedPage = clonedPrevData.filter((config) => config.id !== id);
			return updatedConfigWithDeletedPage;
		});
	};

	/**
	 * Performs the save operation for page level script config.
	 * @param {Object} payload - The data to be sent to the backend for saving.
	 * @param {string} mode - The current mode of the page level script config
	 * @returns {Promise<Object>} A promise that resolves to the response from the backend.
	 */
	const performPageLevelScriptConfigSave = async (payload, mode) => {
		if (mode === PAGE_LEVEL_SCRIPT_CONFIG_MODES.CREATE_PAGE_CONFIG) {
			return await createPageLevelScriptConfig(payload);
		} else {
			return await updatePageLevelScriptConfig(payload);
		}
	};
	/**
	 * Handles the backend response for page level script config operations.
	 * @param {Object} data - The response data from the backend.
	 * @param {Object} cleanedConfig - The cleaned and processed page level script config.
	 * @throws {Error} Throws an error if the backend response is unsuccessful.
	 */
	const handlePageLevelScriptConfigResponse = (data, cleanedConfig) => {
		if (!data?.success) {
			throw new Error(data?.data?.message || 'Unknown error occurred');
		}
		toast.success(data?.data?.message, { position: toast.POSITION.BOTTOM_RIGHT });
		setPageLevelScriptConfigData(cleanedConfig);
		previousPageLevelScriptCouchBaseConfig.current = cleanedConfig;
		setPageLevelScriptConfigMode(PAGE_LEVEL_SCRIPT_CONFIG_MODES.SITE_LEVEL_UPDATE_PAGE_CONFIGS);
	};

	/**
	 * Saves the current page level script config.
	 * Validates, cleans, and sends the current config to the backend,
	 *  handling the response accordingly.
	 */
	const savePageLevelScriptConfigPages = async () => {
		try {
			const cleanedConfig = cleanPageLevelScriptConfigData(pageLevelScriptConfig);

			const payload = {
				data: cleanedConfig,
				siteId,
				siteDomain: siteRawConfig?.siteDomain || '',
				previousCouchBaseConfig: previousPageLevelScriptCouchBaseConfig.current,
				mode: pageLevelScriptConfigMode,
				user
			};

			const { data } = await performPageLevelScriptConfigSave(payload, pageLevelScriptConfigMode);
			handlePageLevelScriptConfigResponse(data, cleanedConfig);
		} catch (err) {
			const requestError = err?.response?.data?.data?.message || err.message;
			toast.error(requestError, { position: toast.POSITION.BOTTOM_RIGHT });
		}
	};

	/**
	 * Syncs a single page's script config on CDN
	 * @param {string} pageUrl - The URL of the page to sync.
	 */
	const handlePageLevelScriptConfigSinglePageSync = async (pageUrl) => {
		try {
			const siteDomain = siteRawConfig?.siteDomain || '';
			const payload = {
				data: previousPageLevelScriptCouchBaseConfig.current,
				pageUrl,
				siteId,
				siteDomain,
				previousCouchBaseConfig: previousPageLevelScriptCouchBaseConfig.current,
				mode: PAGE_LEVEL_SCRIPT_CONFIG_MODES.SYNC_SINGLE_PAGE_LEVEL_CONFIG
			};
			await syncSinglePageLevelScriptConfig(payload);
			toast.success('Page Sync successful !!', { position: toast.POSITION.BOTTOM_RIGHT });
		} catch (err) {
			const requestError = err?.response?.data?.data?.message;
			toast.error(requestError || err?.message, { position: toast.POSITION.BOTTOM_RIGHT });
		}
	};

	/**
	 * Syncs all pages' script configs in bulk on CDN
	 */
	const handlePageLevelScriptConfigBulkPageSync = async () => {
		try {
			const siteDomain = siteRawConfig?.siteDomain || '';
			const payload = {
				data: pageLevelScriptConfig,
				siteId,
				siteDomain,
				previousCouchBaseConfig: pageLevelScriptConfig,
				mode: PAGE_LEVEL_SCRIPT_CONFIG_MODES.SYNC_BULK_PAGE_LEVEL_CONFIG
			};
			const response = await syncBulkPageLevelScriptConfig(payload);
			toast.success('Bulk Page Sync successful !!', { position: toast.POSITION.BOTTOM_RIGHT });
		} catch (err) {
			const requestError = err?.response?.data?.data?.message;
			toast.error(requestError || err?.message, { position: toast.POSITION.BOTTOM_RIGHT });
		}
	};

	/**
	 * Updates the section level split config for a
	 * specific page in the page level script config.
	 * @param {Object} newSplitConfig - The new section level split config.
	 * @param {string} currentPageId - The ID of the page whose config is to be updated.
	 */
	const updateSectionLevelSplitForPageLevelConfig = (newSplitConfig, currentPageId) => {
		setPageLevelScriptConfigData((previousConfig) => {
			const updatedPageLevelScriptConfig = previousConfig.map((pageLevelConfigForCurrentPage) => {
				const clonedPageLevelConfigForCurrentPage = structuredClone(pageLevelConfigForCurrentPage);
				if (clonedPageLevelConfigForCurrentPage.id === currentPageId) {
					clonedPageLevelConfigForCurrentPage.pageLevelScriptConfig.sectionLevelSplit =
						newSplitConfig;
				}
				return clonedPageLevelConfigForCurrentPage;
			});
			return updatedPageLevelScriptConfig;
		});
	};

	return (
		<pageLevelScriptConfigContext.Provider
			value={{
				siteId,
				pageLevelScriptConfig,
				pageLevelScriptConfigMode,
				setPageLevelScriptConfigMode,
				handlePageLevelScriptConfigSinglePageSync,
				handlePageLevelScriptConfigBulkPageSync,
				savePageLevelScriptConfigPages,
				pageLevelScriptConfigPage,
				setPageLevelScriptConfigPage,
				handlePageLevelScriptConfigAddPage,
				setPageLevelScriptConfigData,
				updateSectionLevelSplitForPageLevelConfig,
				deletePageLevelScriptConfigPage
			}}
		>
			{children}
		</pageLevelScriptConfigContext.Provider>
	);
};
/**
 * Custom hook to access the page level script config context.
 * @returns {Object} The context object containing page level script config functions.
 */
const usePageLevelScriptConfigContext = () => useContext(pageLevelScriptConfigContext);

export { PageLevelScriptConfigProvider, usePageLevelScriptConfigContext };
